import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCsrfToken } from "../services/authService";
import { useAuth } from "../hooks/useAuth";
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
} from "libphonenumber-js";
import "../styles/EditProfile.css";

export default function EditProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const countries = getCountries();

  const countryLabels = new Intl.DisplayNames(["es"], {
    type: "region",
  });

  const [form, setForm] = useState({
    email: "",
    name: "",
    lastname1: "",
    lastname2: "",
    country: "ES",
    phone: "",
  });

  const selectedPrefix = `+${getCountryCallingCode(form.country)}`;

  const [error, setError] = useState("");

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    const response = await fetch("/api/profile/me", {
      credentials: "include",
    });

    if (!response.ok) {
      navigate("/login", { replace: true });
      return;
    }

    const data = await response.json();
    setForm({
      email: data.email || "",
      name: data.name || "",
      lastname1: data.lastname1 || "",
      lastname2: data.lastname2 || "",
      country: data.country || "ES",
      phone: data.phone ? data.phone.replace(/\D/g, "").replace(/^34/, "") : "",
    });
  };

  const handlePhoneChange = (e) => {
    const onlyNumbers = e.target.value.replace(/\D/g, "");

    setForm({
      ...form,
      phone: onlyNumbers,
    });
  };

  const handleCountryChange = (e) => {
    setForm({
      ...form,
      country: e.target.value,
      phone: "",
    });
  };

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const csrf = await getCsrfToken();
      const fullPhone = `${selectedPrefix}${form.phone}`;

      try {
        if (!isValidPhoneNumber(fullPhone, form.country)) {
          setError(
            t("editProfile.invalidPhone") ||
              "Introduce un número de teléfono válido."
          );
          return;
        }
      } catch {
        setError(
          t("editProfile.invalidPhone") ||
            "Introduce un número de teléfono válido."
        );
        return;
      }

      const response = await fetch("/api/profile/me", {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [csrf.headerName]: csrf.token,
        },
        body: JSON.stringify({
          ...form,
          phone: fullPhone,
        }),
      });

      if (response.status === 409) {
        setError(t("editProfile.emailExists"));
        return;
      }

      if (!response.ok) {
        setError(t("editProfile.error"));
        return;
      }

      await refreshUser();

      navigate("/profile", {
        replace: true,
        state: {
          profileUpdated: true,
        },
      });
    } catch {
      setError(t("editProfile.error"));
    }
  };

  return (
    <main className="profile-page">
      <section className="profile-card">
        <h1>{t("editProfile.title")}</h1>

        <p className="profile-description">
          {t("editProfile.description")}
        </p>

        <form className="profile-form" onSubmit={handleSubmit}>
          <label>
            {t("editProfile.email")}
            <input type="email" name="email" value={form.email} onChange={handleChange} required />
          </label>

          <label>
            {t("editProfile.name")}
            <input name="name" value={form.name} onChange={handleChange} required />
          </label>

          <label>
            {t("editProfile.lastname1")}
            <input name="lastname1" value={form.lastname1} onChange={handleChange} required />
          </label>

          <label>
            {t("editProfile.lastname2")}
            <input name="lastname2" value={form.lastname2} onChange={handleChange} />
          </label>

          <label>
            {t("editProfile.phone")}

            <div className="phone-row">
              <select
                name="country"
                value={form.country}
                onChange={handleCountryChange}
              >
                {countries.map((country) => (
                  <option key={country} value={country}>
                    +{getCountryCallingCode(country)} {countryLabels.of(country)}
                  </option>
                ))}
              </select>

              <input
                name="phone"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                value={form.phone}
                onChange={handlePhoneChange}
                required
              />
            </div>
          </label>

          {error && <p className="login-error">{error}</p>}

          <div className="profile-actions">
            <button type="submit">{t("editProfile.save")}</button>
            <button type="button" onClick={() => navigate("/profile")}>
              {t("editProfile.back")}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}